// pages/Search.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import BrandCard from '../components/BrandCard';
import SuccessAlert from '../components/SuccessAlert';

function SearchPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBonusAlert, setShowBonusAlert] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch brands from Firebase
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        
        // Get all brands from the collection
        const brandsRef = collection(db, "brands");
        const querySnapshot = await getDocs(brandsRef);
        
        // Map the results to our brands array
        const brandsData = [];
        querySnapshot.forEach((doc) => {
          brandsData.push({ id: doc.id, ...doc.data() });
        });
        
        // Update state with fetched brands
        setBrands(brandsData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching brands:", err);
        setError("Failed to load brands");
        setLoading(false);
      }
    };
    
    fetchBrands();
  }, []);
  
  const handleClaimBonus = (brand) => {
    setSelectedBrand(brand);
    setShowBonusAlert(true);
    
    // Auto close after 5 seconds
    setTimeout(() => {
      setShowBonusAlert(false);
    }, 5000);
  };
  
  // Filter brands based on search term
  const filteredBrands = brands.filter(brand => 
    brand.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brand.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brand.welcomeBonus?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="pb-20"> {/* Padding bottom for the bottom tabs */}
      {/* Success Alert */}
      {showBonusAlert && selectedBrand && (
        <SuccessAlert 
          brand={selectedBrand} 
          onClose={() => setShowBonusAlert(false)} 
        />
      )}
      
      {/* Search Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Search Brands</h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search brands, bonuses, or features..."
              className="w-full py-3 px-4 pr-10 rounded-lg text-gray-700"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute right-3 top-3 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Search Results */}
      <div className="max-w-6xl mx-auto py-4 px-4">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
            <p className="text-red-700">{error}</p>
            <p className="text-sm text-red-600 mt-1">Please try refreshing the page.</p>
          </div>
        ) : (
          <>
            {searchTerm && (
              <p className="text-gray-600 mb-4">
                Found {filteredBrands.length} {filteredBrands.length === 1 ? 'result' : 'results'} for "{searchTerm}"
              </p>
            )}
            
            <div className="grid grid-cols-1 gap-6">
              {searchTerm ? (
                filteredBrands.length > 0 ? (
                  filteredBrands.map(brand => (
                    <BrandCard 
                      key={brand.id} 
                      brand={brand} 
                      onClaimBonus={handleClaimBonus} 
                    />
                  ))
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-4 text-gray-600">No results found for "{searchTerm}"</p>
                    <p className="mt-2 text-gray-500">Try different keywords or browse all brands.</p>
                  </div>
                )
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="mt-4 text-gray-600">Start your search</p>
                  <p className="mt-2 text-gray-500">Type to find brands, bonuses or features.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SearchPage;